import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spin, Typography, Tag, Divider, message, Grid, Segmented, Select } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, StarOutlined, HeartOutlined, HeartFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { useCart } from '../context/CartContext';
import ProductGallery from '../components/ProductGallery';
import { Seo, JsonLd } from '../seo';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const BRAND = 'var(--brand-primary, #222957)';

const isChainKey = (k) => /鍊型|鏈型|款式|鍊|鏈/.test(k || '');
// 鍊型優點：用關鍵字比對，容錯「鍊/鏈」「扣/釦」等不同寫法
const chainDescFor = (val) => {
  const s = String(val || '');
  if (/調節|可調|活動/.test(s)) return '可自由調整鬆緊，適合多種手圍';
  if (/磁扣|磁釦|磁/.test(s)) return '磁吸好戴、穩固不易鬆脫';
  if (/彈性|鬆緊|繩/.test(s)) return '好穿脫、有彈性，適合日常快速配戴';
  return null;
};
const PANEL = '#F9FAFD';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';

const getCustomerToken = () => {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
};

// 故事欄位可能是「HTML 排版」或「純文字（含換行的詩句）」：有標籤才當 HTML 渲染，
// 否則用 pre-wrap 保留換行，避免純文字被 innerHTML 吃掉換行擠成一行。
const looksLikeHtml = (s) => /<[a-z][\s\S]*>/i.test(s || '');

// 主石尺寸格式化：已含「約」就不重複加
const fmtSize = (s) => {
  const v = String(s || '').trim();
  if (!v) return '';
  return /約/.test(v) ? v : `約 ${v}`;
};

// 由「該手鍊自己的資料」自動組主石資訊列（主石／尺寸／搭配水晶／鍊型）
const buildStoneRows = (product, chainNote) => {
  const rows = [];
  if (product.main_stone) rows.push(['主石', product.main_stone]);
  const size = fmtSize(product.main_stone_size);
  if (size) rows.push(['主石尺寸', size]);
  // 搭配水晶＝該款物料清單的水晶，排除已列在「主石」的那顆，避免重複
  const mainStone = String(product.main_stone || '').trim();
  const crystals = (Array.isArray(product.crystals) ? product.crystals : [])
    .filter(Boolean)
    .filter((c) => String(c).trim() !== mainStone);
  if (crystals.length) rows.push(['搭配水晶', crystals.join('｜')]);
  // 鍊型：優先用該商品自己的「照片鍊型」組成；沒設定才退回全站共用說明
  const chainRow = product.photo_chain_type
    ? `照片為${product.photo_chain_type}示意款（可依配戴習慣，客製更換為彈性繩／延長鍊／磁扣鍊）`
    : (chainNote && chainNote.trim() ? chainNote.trim() : '');
  if (chainRow) rows.push(['鍊型', chainRow]);
  return rows;
};

// 蒐集所有變體用到的規格欄位（依出現順序，如 ["手圍","款式"]）
function specKeysFrom(variants) {
  const keys = [];
  variants.forEach((v) => Object.keys(v.spec || {}).forEach((k) => {
    if (!keys.includes(k)) keys.push(k);
  }));
  return keys;
}
// 每個規格欄位的可選值（去重、依出現順序）
function optionsFrom(variants, specKeys) {
  const m = {};
  specKeys.forEach((k) => { m[k] = []; });
  variants.forEach((v) => specKeys.forEach((k) => {
    const val = (v.spec || {})[k];
    if (val != null && val !== '' && !m[k].includes(val)) m[k].push(val);
  }));
  return m;
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { add } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // 變體
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // 願望清單
  const [wished, setWished] = useState(false);
  const [wishBusy, setWishBusy] = useState(false);

  // 圖片輪播目前索引

  // 鍊型說明（所有手鍊共用一句）＋出貨/售後說明（全站共用）；後台「商品管理 → 商品說明（共用）」可改
  const [chainNote, setChainNote] = useState('');
  const [formImages, setFormImages] = useState({}); // 後台可設圖片（含手圍量測教學圖 wrist_guide）
  const [wristGuideOpen, setWristGuideOpen] = useState(false); // 手圍教學圖放大檢視
  const [sharedInfo, setSharedInfo] = useState('');

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setVariants([]);
    setSelectedVariant(null);
    fetch(`${API_BASE}/api/catalog/public/products/${id}`)
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(setProduct)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    // 變體（有就顯示選擇器）
    fetch(`${API_BASE}/api/variants/public/products/${id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setVariants(arr);
        // 預選第一個有庫存的變體，否則第一個
        if (arr.length > 0) {
          setSelectedVariant(arr.find((v) => (v.stock || 0) > 0) || arr[0]);
        }
      })
      .catch(() => setVariants([]));
  }, [id]);

  // 預先載入所有輪播圖片，讓左右切換即時、不會閃一下（灰底→圖）
  useEffect(() => {
    const urls = (product && Array.isArray(product.image_urls) && product.image_urls.length)
      ? product.image_urls
      : (product && product.image_url ? [product.image_url] : []);
    urls.forEach((u) => { if (u) { const im = new window.Image(); im.src = u; } });
  }, [product]);

  // 共用說明（鍊型說明 + 出貨/售後）：一次載入，所有手鍊一致
  useEffect(() => {
    fetch(`${API_BASE}/api/settings/shop`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        setChainNote((d && d.product_chain_note) || '');
        setSharedInfo((d && d.product_shared_info) || '');
      })
      .catch(() => {});
  }, []);

  // 後台可設圖片（手圍量測教學圖）
  useEffect(() => {
    fetch(`${API_BASE}/api/settings/form-images`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setFormImages(d || {}))
      .catch(() => {});
  }, []);

  // 願望清單目前狀態（best-effort，需登入）
  const refreshWishState = useCallback(() => {
    const token = getCustomerToken();
    if (!token) { setWished(false); return; }
    fetch(`${API_BASE}/api/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const arr = Array.isArray(list) ? list : (list?.items || []);
        const pid = Number(id);
        setWished(arr.some((w) => Number(w.product_id ?? w.odoo_product_id ?? w) === pid));
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => { refreshWishState(); }, [refreshWishState]);

  // 下架（後端回 404）或商品不存在 → 直接導回首頁（隱藏商品仍會正常載入，不受影響）
  useEffect(() => {
    if (notFound) navigate('/', { replace: true });
  }, [notFound, navigate]);

  const containerStyle = {
    maxWidth: isMobile ? '100%' : 1120,
    margin: '0 auto',
    padding: isMobile ? '16px' : '24px',
  };

  if (loading || notFound || !product) {
    return <div style={{ textAlign: 'center', padding: '80px 0' }}><Spin /></div>;
  }

  // 主石資訊列（該手鍊自己的資料自動組）
  const stoneRows = buildStoneRows(product, chainNote);

  // 輪播圖片陣列：優先多圖 image_urls，退回單張 image_url
  const gallery = (Array.isArray(product.image_urls) && product.image_urls.length > 0)
    ? product.image_urls
    : (product.image_url ? [product.image_url] : []);

  const hasVariants = variants.length > 0;
  // 現行售價 / 庫存：有選變體用變體的（售價留空則沿用商品價），否則用商品本身
  const activePrice = hasVariants
    ? (selectedVariant?.price != null ? selectedVariant.price : product.price)
    : product.price;
  const activeStock = hasVariants
    ? (selectedVariant?.stock || 0)
    : (product.stock || 0);
  const inStock = activeStock > 0;

  // 規格拆成「多個選項群組」（如 手圍 / 款式 各一排），而非把每個變體整條展開
  const specKeys = specKeysFrom(variants);
  const optionsByKey = optionsFrom(variants, specKeys);
  const hasSpecGroups = specKeys.length > 0;
  const sel = selectedVariant?.spec || {};

  // 某 key=val 在「其他 key 維持現選」下是否有對應變體 / 是否有庫存
  const comboMatches = (v, key, val) =>
    (v.spec || {})[key] === val &&
    specKeys.every((k) => k === key || sel[k] == null || (v.spec || {})[k] === sel[k]);
  const valueInStock = (key, val) => variants.some((v) => comboMatches(v, key, val) && (v.stock || 0) > 0);

  // 選某選項：挑「含此值、優先有庫存、且與現有其他選擇最相符」的整條變體
  const pickValue = (key, val) => {
    const candidates = variants.filter((v) => (v.spec || {})[key] === val);
    if (!candidates.length) return;
    const inStockC = candidates.filter((v) => (v.stock || 0) > 0);
    const pool = inStockC.length ? inStockC : candidates;
    let best = pool[0];
    let bestScore = -1;
    pool.forEach((v) => {
      let score = 0;
      specKeys.forEach((k) => { if (k !== key && (v.spec || {})[k] === sel[k]) score += 1; });
      if (score > bestScore) { bestScore = score; best = v; }
    });
    setSelectedVariant(best);
  };

  // 帶著這條手鍊進客製流程當「參考手鍊」，直接進微調
  const goCustomWithRef = () => {
    navigate('/custom', {
      state: {
        refBracelet: {
          id: product.odoo_product_id,
          name: product.name,
          image_url: product.image_url || gallery[0] || null,
          min_variant_price: activePrice != null ? activePrice : product.price,
          main_stone: product.main_stone,
          crystal_main_color: product.crystal_main_color,
          crystal_accent_color: product.crystal_accent_color,
          story: product.story,
          pinkoi_url: product.pinkoi_url,
        },
      },
    });
  };

  const handleAdd = () => {
    if (!inStock) return;
    add({
      product_id: product.odoo_product_id,
      variant_id: hasVariants ? (selectedVariant?.id ?? null) : null,
      name: product.name,
      spec_label: hasVariants ? (selectedVariant?.spec_label || '') : '',
      price: activePrice || 0,
      qty: 1,
      image_url: product.image_url || null,
    }, 1);
    message.success('已加入購物車');
  };

  const toggleWish = async () => {
    const token = getCustomerToken();
    if (!token) {
      window.location.href = `${API_BASE}/api/auth/google/customer/login?next=${encodeURIComponent(`/product/${id}`)}`;
      return;
    }
    setWishBusy(true);
    const willAdd = !wished;
    try {
      const res = willAdd
        ? await fetch(`${API_BASE}/api/wishlist`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ product_id: product.odoo_product_id }),
          })
        : await fetch(`${API_BASE}/api/wishlist/${product.odoo_product_id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
      if (!res.ok) throw new Error();
      setWished(willAdd);
      message.success(willAdd ? '已加入收藏' : '已取消收藏');
    } catch {
      message.error('收藏操作失敗，請稍後再試');
    } finally {
      setWishBusy(false);
    }
  };

  const seoDesc = String(product.description || (product.story || '').replace(/<[^>]+>/g, ' '))
    .replace(/\s+/g, ' ').trim().slice(0, 120);

  // Product / Offer 結構化資料：讓 Google 商品結果與 AI 正確理解商品（名稱/圖/價/幣別/庫存/品牌/材質）。
  const SITE_ORIGIN = 'https://shiningstars.tw';
  const absUrl = (u) => {
    const s = String(u || '').trim();
    if (!s) return '';
    if (/^https?:\/\//.test(s)) return s;
    return SITE_ORIGIN + (s.startsWith('/') ? '' : '/') + s;
  };
  const ldImages = (gallery.length ? gallery : [product.image_url]).map(absUrl).filter(Boolean);
  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: seoDesc,
    ...(ldImages.length ? { image: ldImages } : {}),
    brand: { '@type': 'Brand', name: '星河耀眼 ShiningStar Jewelry' },
    ...(product.main_stone ? { material: product.main_stone } : {}),
    sku: String(product.odoo_product_id || id),
    ...(activePrice != null ? {
      offers: {
        '@type': 'Offer',
        price: String(activePrice),
        priceCurrency: 'TWD',
        availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: `${SITE_ORIGIN}/product/${id}`,
        seller: { '@type': 'Organization', name: '星河耀眼 ShiningStar Jewelry' },
      },
    } : {}),
  };

  return (
    <div style={containerStyle}>
      <Seo
        page={product.name}
        description={seoDesc}
        image={gallery[0] || product.image_url}
        path={`/product/${id}`}
        type="product"
      />
      <JsonLd id="product" data={productLd} />
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/shop')} style={{ marginBottom: 12, paddingLeft: 0 }}>
        回商品列表
      </Button>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 520px) minmax(0, 1fr)',
          gap: isMobile ? 20 : 40,
          alignItems: 'start',
        }}
      >
        <div style={{ position: isMobile ? 'static' : 'sticky', top: 24 }}>
          <ProductGallery key={id} images={gallery} alt={product.name} isMobile={isMobile} brand={BRAND} border={BORDER} />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Title level={isMobile ? 3 : 2} style={{ marginTop: 0, marginBottom: 8, flex: 1, color: HEADING, fontWeight: 800 }}>{product.name}</Title>
            <Button
              type="text"
              shape="circle"
              loading={wishBusy}
              onClick={toggleWish}
              aria-label={wished ? '取消收藏' : '加入收藏'}
              icon={wished
                ? <HeartFilled style={{ color: '#eb2f96', fontSize: 24 }} />
                : <HeartOutlined style={{ color: '#eb2f96', fontSize: 24 }} />}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            {product.crystal_main_color && <Tag color="geekblue">{product.crystal_main_color}</Tag>}
            {!inStock && <Tag color="red">補貨中</Tag>}
          </div>

          {/* 主石資訊：放在商品名稱下方（由該手鍊自己的資料自動組：主石／尺寸／搭配水晶／鍊型） */}
          {stoneRows.length > 0 && (
            <div
              style={{
                marginBottom: 16, padding: '14px 18px', borderRadius: 12,
                background: PANEL, border: `1px solid ${BORDER}`,
                color: '#2B3470', fontSize: 14.5, lineHeight: 1.9,
              }}
            >
              {stoneRows.map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                  <span style={{ flex: '0 0 auto', fontWeight: 700, color: HEADING }}>✦ {label}：</span>
                  <span style={{ flex: 1 }}>{value}</span>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              display: 'inline-block',
              padding: '10px 18px',
              borderRadius: 14,
              background: PANEL,
              border: `1px solid ${BORDER}`,
            }}
          >
            <Text style={{ color: BRAND, fontWeight: 800, fontSize: isMobile ? 28 : 32, lineHeight: 1 }}>
              {activePrice != null ? `NT$ ${activePrice}` : '—'}
            </Text>
          </div>

          {/* 規格選擇：手圍用下拉選單；其餘（鍊型等）用品牌感 Segmented。沒貨的選項直接 disable */}
          {hasVariants && hasSpecGroups && (
            <div style={{ marginTop: 20 }}>
              {specKeys.map((key) => {
                const opts = optionsByKey[key].map((val) => ({
                  label: val, value: val, disabled: !valueInStock(key, val),
                }));
                const isWrist = /手圍|尺寸|圍|cm/i.test(key);
                const isChain = isChainKey(key);
                const chainDesc = isChain ? chainDescFor(sel[key]) : null;
                return (
                  <div key={key} style={{ marginBottom: 18 }}>
                    {/* 標題只放規格名（值由下拉/Segmented 呈現，不重複） */}
                    <Text strong style={{ display: 'block', marginBottom: chainDesc ? 4 : 8 }}>{key}</Text>
                    {/* 鍊型：所選款式的優點，放在標題的下一行 */}
                    {chainDesc && (
                      <div style={{ fontSize: 16, color: '#2B3470', fontWeight: 500, lineHeight: 1.7, marginBottom: 12 }}>
                        {chainDesc}
                      </div>
                    )}
                    {isWrist ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Select
                          size="large"
                          style={{ width: isMobile ? '100%' : 220 }}
                          placeholder={`選擇${key}`}
                          value={sel[key] ?? undefined}
                          onChange={(val) => pickValue(key, val)}
                          options={opts}
                        />
                        {/* 手圍量測教學：點開跳出教學圖（後台設 wrist_guide 才顯示） */}
                        {formImages.wrist_guide && (
                          <Button
                            size="large"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => setWristGuideOpen(true)}
                            style={{ borderColor: BRAND, color: BRAND }}
                          >
                            手圍怎麼量？
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Segmented
                        className="brand-segmented"
                        block
                        size="large"
                        value={sel[key]}
                        onChange={(val) => pickValue(key, val)}
                        options={opts}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 無規格欄位（單一 SKU 型變體）時：與購物車一致用 Segmented；沒貨的直接 disable */}
          {hasVariants && !hasSpecGroups && (
            <div style={{ marginTop: 20 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>選擇規格</Text>
              <Segmented
                className="brand-segmented"
                block
                value={selectedVariant?.id}
                onChange={(vid) => { const v = variants.find((x) => x.id === vid); if (v) setSelectedVariant(v); }}
                options={variants.map((v) => ({
                  label: v.spec_label || '規格',
                  value: v.id,
                  disabled: (v.stock || 0) <= 0 && selectedVariant?.id !== v.id,
                }))}
              />
            </div>
          )}

          <Button
            type="primary"
            size="large"
            block
            icon={<ShoppingCartOutlined />}
            onClick={handleAdd}
            disabled={!inStock}
            style={{ height: 50, fontSize: 16, marginTop: 24, borderRadius: 12, background: inStock ? BRAND : undefined, borderColor: inStock ? BRAND : undefined }}
          >
            加入購物車
          </Button>

          <Divider style={{ margin: '20px 0' }}>或</Divider>
          <Button block size="large" icon={<StarOutlined />} onClick={goCustomWithRef} style={{ height: 48, borderRadius: 12, borderColor: BRAND, color: BRAND }}>
            我想客製專屬能量手鏈
          </Button>
        </div>
      </div>

      {/* 商品說明（共用）：故事直接放在這塊的最上面，接著出貨/售後等共用內容（同一個區塊，不獨立） */}
      {(product.story || product.description || sharedInfo) && (
        <div
          style={{
            marginTop: isMobile ? 28 : 40,
            padding: isMobile ? '18px 18px' : '26px 30px',
            borderRadius: 16, background: '#fff',
            border: `1px solid ${BORDER}`,
          }}
        >
          {/* 故事（最上面；純文字保留換行，HTML 依後台排版渲染） */}
          {(product.story || product.description) && (
            <>
              {/* 故事標題（樣式對齊共用說明的小標） */}
              <h3 style={{ fontSize: 16, fontWeight: 800, color: HEADING, margin: '0 0 10px', paddingLeft: 10, borderLeft: `4px solid ${BRAND}`, lineHeight: 1.4 }}>
                商品故事
              </h3>
              {product.story ? (
                looksLikeHtml(product.story) ? (
                  <div
                    className="product-story-html"
                    style={{ color: '#3B489B', fontSize: 15, lineHeight: 1.9 }}
                    dangerouslySetInnerHTML={{ __html: product.story }}
                  />
                ) : (
                  <Paragraph style={{ color: '#3B489B', fontSize: 15, lineHeight: 1.9, whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                    {product.story}
                  </Paragraph>
                )
              ) : (
                <Paragraph style={{ color: '#3B489B', fontSize: 15, lineHeight: 1.9, whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                  {product.description}
                </Paragraph>
              )}
            </>
          )}

          {/* 出貨附贈 / 購買須知 / 保固 / 保養（後台可改） */}
          {sharedInfo && (
            <div
              className="product-info-html"
              style={{
                marginTop: (product.story || product.description) ? 20 : 0,
                color: '#323D85', lineHeight: 1.9, fontSize: 14.5,
              }}
              dangerouslySetInnerHTML={{ __html: sharedInfo }}
            />
          )}
        </div>
      )}

      {/* 手圍量測教學圖：全螢幕放大檢視（點任意處關閉） */}
      {wristGuideOpen && formImages.wrist_guide && createPortal(
        <div
          onClick={() => setWristGuideOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'auto', WebkitOverflowScrolling: 'touch',
          }}
        >
          <button
            onClick={() => setWristGuideOpen(false)}
            aria-label="關閉"
            style={{
              position: 'fixed', top: 16, right: 16, zIndex: 1, width: 40, height: 40,
              borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)',
              color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer',
            }}
          >×</button>
          <img
            src={formImages.wrist_guide} alt="手圍量測教學"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 900, height: 'auto', touchAction: 'pinch-zoom', display: 'block' }}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
